import {
  CustomerOrderDetailsDto,
  OrdersApi,
  PaymeraProviderStatus,
  PaymeraReconciliationDto,
} from '@/app/services/orders.api';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PaymentStatusBanner } from '@/app/layouts/payment-status-banner/payment-status-banner';
import { EmptyPage } from '@/app/layouts/empty-page/empty-page';

interface OrderStatusBannerViewModel {
  imageSrc: string;
  message: string;
  title: string;
  buttonText: string;
  buttonLink: string[] | null;
  secondaryButtonText: string;
  secondaryButtonLink: string[] | null;
}

type PaymentStatusView = 'Done' | 'Cancelled' | 'Pending';

@Component({
  selector: 'app-order-success-page',
  standalone: true,
  imports: [CommonModule, PaymentStatusBanner, EmptyPage],
  templateUrl: './order-success-page.html',
  styleUrl: './order-success-page.css',
})
export class OrderSuccessPage implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly ordersApi = inject(OrdersApi);

  readonly isLoading = signal(true);
  readonly isActionInFlight = signal(false);
  readonly banner = signal<OrderStatusBannerViewModel>({
    imageSrc: 'assets/images/Pending.svg',
    message: 'We are checking your payment status.',
    title: 'Payment is still pending',
    buttonText: '',
    buttonLink: null,
    secondaryButtonText: '',
    secondaryButtonLink: null,
  });
  private readonly reconciliation = signal<PaymeraReconciliationDto | null>(null);
  private readonly orderDetails = signal<CustomerOrderDetailsDto | null>(null);
  private readonly statusView = signal<PaymentStatusView>('Pending');
  private loadingStartedAt = 0;
  private loadingTimer?: ReturnType<typeof setTimeout>;

  ngOnInit() {
    const attemptToken = this.route.snapshot.queryParamMap.get('attempt')?.trim();
    const orderId = history.state.orderId as string | undefined;

    if (attemptToken) {
      this.loadReconciliation(attemptToken);
      return;
    }

    if (orderId) {
      this.banner.set(this.buildLegacySuccessBanner(orderId));
      this.isLoading.set(false);
      return;
    }

    this.banner.set({
      imageSrc: 'assets/images/paused.svg',
      message:
        'No payment attempt was provided. Please return to your orders and try again.',
      buttonText: 'Continue shopping',
      title: 'Payment is cancelled',

      buttonLink: ['/'],
      secondaryButtonText: 'My Orders',
      secondaryButtonLink: ['/profile', 'orders'],
    });
    this.isLoading.set(false);
  }

  ngOnDestroy() {
    clearTimeout(this.loadingTimer);
  }

  private loadReconciliation(attemptToken: string) {
    this.loadingStartedAt = Date.now();
    this.isLoading.set(true);

    this.ordersApi.reconcilePaymeraAttempt(attemptToken).subscribe({
      next: (reconciliation) => {
        this.reconciliation.set(reconciliation);
        this.loadOrderDetailsForBanner(reconciliation);
      },
      error: (error) => {
        const errorCode = error?.error?.error?.code ?? error?.error?.code ?? null;

        this.banner.set(this.buildErrorBanner(errorCode));
        this.finishLoading();
      },
    });
  }

  private loadOrderDetailsForBanner(reconciliation: PaymeraReconciliationDto) {
    this.ordersApi.getOrderDetails(reconciliation.orderId).subscribe({
      next: (orderDetails) => {
        this.orderDetails.set(orderDetails);
        this.banner.set(this.buildReconciliationBanner(reconciliation, orderDetails));
        this.finishLoading();
      },
      error: () => {
        this.orderDetails.set(null);
        this.banner.set(this.buildReconciliationBanner(reconciliation, null));
        this.finishLoading();
      },
    });
  }

  private finishLoading() {
    const remainingDuration = Math.max(
      0,
      3_000 - (Date.now() - this.loadingStartedAt),
    );

    if (remainingDuration === 0) {
      this.isLoading.set(false);
      return;
    }

    this.loadingTimer = setTimeout(
      () => this.isLoading.set(false),
      remainingDuration,
    );
  }

  private buildLegacySuccessBanner(orderId: string): OrderStatusBannerViewModel {
    return {
      imageSrc: 'assets/images/Done.svg',
      message: 'Your order was placed successfully.',
      title: 'Order is placed',
      buttonText: 'View Order',
      buttonLink: ['/order-details', orderId],
      secondaryButtonText: 'Continue shopping',
      secondaryButtonLink: ['/'],
    };
  }

  private buildErrorBanner(errorCode: string | null): OrderStatusBannerViewModel {
    if (errorCode === 'Sham.Payments.PaymeraAttemptTokenMissing') {
      return {
        imageSrc: 'assets/images/paused.svg',
        message: 'The payment return link is missing its attempt token.',
        title: 'Order Is Paused',
        buttonText: 'My Orders',
        buttonLink: ['/profile', 'orders'],
        secondaryButtonText: 'Continue shopping',
        secondaryButtonLink: ['/'],
      };
    }

    if (errorCode === 'Sham.Payments.PaymentAttemptNotFound') {
      return {
        imageSrc: 'assets/images/paused.svg',
        message:
          'We could not find this payment attempt. Please open your order and try again.',
        title: 'Order Is Paused',
        buttonText: 'My Orders',
        buttonLink: ['/profile', 'orders'],
        secondaryButtonText: 'Continue shopping',
        secondaryButtonLink: ['/'],
      };
    }

    return {
      imageSrc: 'assets/images/paused.svg',
      message:
        'We could not verify your payment right now. Please refresh your order status at a moment.',
      title: 'Order Is Paused',
      buttonText: 'My Orders',
      buttonLink: ['/profile', 'orders'],
      secondaryButtonText: 'Continue Shopping',
      secondaryButtonLink: ['/'],
    };
  }

  private buildReconciliationBanner(
    reconciliation: PaymeraReconciliationDto,
    orderDetails: CustomerOrderDetailsDto | null,
  ): OrderStatusBannerViewModel {
    const statusView = this.resolveStatusView(reconciliation, orderDetails);
    this.statusView.set(statusView);
    const primaryAction = this.buildPrimaryAction(statusView, reconciliation.orderId);
    const secondaryAction = this.buildSecondaryAction(
      statusView,
      reconciliation.orderId,
    );
    const fallbackMessage = this.buildStatusMessage(statusView);

    const fallbackTitle = this.buildStatusTitle(statusView);

    return {
      imageSrc: this.getImageForStatus(statusView),
      message: reconciliation.message?.trim() || fallbackMessage,
      title: fallbackTitle,
      buttonText: primaryAction.text,
      buttonLink: primaryAction.link,
      secondaryButtonText: secondaryAction.text,
      secondaryButtonLink: secondaryAction.link,
    };
  }

  private buildPrimaryAction(statusView: PaymentStatusView, orderId: string) {
    if (statusView === 'Done') {
      return {
        text: 'View Order',
        link: ['/order-details', orderId],
      };
    }

    if (statusView === 'Cancelled') {
      return {
        text: 'Try Payment Again',
        link: ['/order-details', orderId],
      };
    }

    return {
      text: 'Continue Payment',
      link: ['/order-details', orderId],
    };
  }

  private buildSecondaryAction(statusView: PaymentStatusView, orderId: string) {
    if (statusView === 'Done') {
      return {
        text: 'Continue shopping',
        link: ['/'],
      };
    }

    return {
      text: 'View Order',
      link: ['/order-details', orderId],
    };
  }

  private resolveStatusView(
    reconciliation: PaymeraReconciliationDto,
    orderDetails: CustomerOrderDetailsDto | null,
  ): PaymentStatusView {
    if (
      reconciliation.reconciliationOutcome === 'Confirmed' &&
      reconciliation.providerStatus === 'A'
    ) {
      return 'Done';
    }

    if (
      reconciliation.providerStatus === 'F' ||
      reconciliation.providerStatus === 'C' ||
      reconciliation.localStatus.toLowerCase() === 'failed' ||
      reconciliation.localStatus.toLowerCase() === 'canceled' ||
      reconciliation.localStatus.toLowerCase() === 'cancelled' ||
      orderDetails?.canRetry
    ) {
      return 'Cancelled';
    }

    return 'Pending';
  }

  private getImageForStatus(statusView: PaymentStatusView) {
    switch (statusView) {
      case 'Done':
        return 'assets/images/Done.svg';
      case 'Cancelled':
        return 'assets/images/Cancelled.svg';
      default:
        return 'assets/images/Pending.svg';
    }
  }

  private buildStatusMessage(statusView: PaymentStatusView) {
    switch (statusView) {
      case 'Done':
        return 'Your order was sent to the sellers';
      case 'Cancelled':
        return 'Your payment process was canceled and no charges were made. You can try paying again or choose a different payment method.';
      default:
        return 'Your order is reserved';
    }
  }
  private buildStatusTitle(statusView: PaymentStatusView) {
    switch (statusView) {
      case 'Done':
        return 'Payment confirmed';
      case 'Cancelled':
        return 'Payment was canceled';
      default:
        return 'Payment is still pending';
    }
  }

  handlePrimaryAction(event: Event) {
    const reconciliation = this.reconciliation();
    if (!reconciliation || this.isActionInFlight()) {
      return;
    }

    const statusView = this.statusView();

    if (statusView === 'Cancelled') {
      event.preventDefault();
      this.retryPayment(reconciliation.orderId);
      return;
    }

    if (statusView === 'Pending' && this.canContinueAwaitingPayment()) {
      event.preventDefault();
      const paymentUrl = this.orderDetails()?.onlinePayment?.paymentUrl?.trim();

      if (paymentUrl) {
        window.location.href = paymentUrl;
      }
    }
  }
  private retryPayment(orderId: string) {
    this.isActionInFlight.set(true);

    this.ordersApi.retryPaymeraPayment(orderId).subscribe({
      next: (response) => {
        this.isActionInFlight.set(false);
        window.location.href = response.hostedPaymentUrl;
      },
      error: () => {
        this.isActionInFlight.set(false);
      },
    });
  }

  private canContinueAwaitingPayment() {
    return (
      this.statusView() === 'Pending' &&
      this.orderDetails()?.onlinePayment?.status === 'AwaitingPayment' &&
      !!this.orderDetails()?.onlinePayment?.paymentUrl
    );
  }
}
